// Copyright (c) Ping. All rights reserved.
//
// Real background blur / replacement using @livekit/track-processors (MediaPipe
// Selfie Segmentation). Segmentation runs on-device; only the composited frames
// are published, so plaintext video never leaves the device (calls.md §15).
// Holds the desired effect so it can be re-applied when the camera track is
// recreated (e.g. after toggling the camera off→on).

import {
  BackgroundBlur,
  type BackgroundProcessorWrapper,
  supportsBackgroundProcessors,
  VirtualBackground,
} from '@livekit/track-processors';
import type { LocalVideoTrack } from 'livekit-client';

export type BgEffectKind = 'none' | 'blur' | 'strong' | 'image';

export interface BgEffect {
  readonly kind: BgEffectKind;
  /** Object URL for a custom virtual-background image (kind === 'image'). */
  readonly imageUrl?: string;
}

let desired: BgEffect = { kind: 'none' };
let processor: BackgroundProcessorWrapper | null = null;
// The camera track the processor is attached to — kept so teardown can call
// `stopProcessor()` and release the MediaPipe segmentation graph + WebGL/wasm
// context (nulling the handle alone leaks that native context per call).
let activeTrack: LocalVideoTrack | null = null;

export function backgroundSupported(): boolean {
  try {
    return supportsBackgroundProcessors();
  } catch {
    return false;
  }
}

export function desiredBackground(): BgEffect {
  return desired;
}

async function apply(track: LocalVideoTrack | undefined, effect: BgEffect): Promise<void> {
  if (!track) return;
  // Tear down any existing processor first (stop+recreate is the most robust
  // path across track-processor versions).
  if (processor) {
    try {
      await track.stopProcessor();
    } catch {
      /* track may already be gone */
    }
    processor = null;
    activeTrack = null;
  }
  if (effect.kind === 'none' || !backgroundSupported()) return;
  if (effect.kind === 'image' && effect.imageUrl) {
    processor = VirtualBackground(effect.imageUrl);
  } else {
    // Light vs strong blur — perceptibly distinct radii while staying smooth on
    // the MediaPipe segmentation graph.
    processor = BackgroundBlur(effect.kind === 'strong' ? 28 : 8);
  }
  await track.setProcessor(processor);
  activeTrack = track;
}

/** Set + apply the desired effect to the current camera track. */
export async function setBackground(
  track: LocalVideoTrack | undefined,
  effect: BgEffect,
): Promise<void> {
  desired = effect;
  await apply(track, effect);
}

/** Re-apply the desired effect (call after the camera track is (re)created). */
export async function reapplyBackground(track: LocalVideoTrack | undefined): Promise<void> {
  if (desired.kind === 'none') return;
  await apply(track, desired);
}

/**
 * Reset to no effect (call on call teardown). Stops the processor on the
 * tracked camera track so the MediaPipe graph + GPU/wasm context is released —
 * MUST run BEFORE the room disconnects the track. Best-effort + idempotent.
 */
export function resetBackground(): void {
  desired = { kind: 'none' };
  const track = activeTrack;
  processor = null;
  activeTrack = null;
  if (track) {
    void track.stopProcessor().catch(() => undefined);
  }
}
